import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { deleteFile, getFilePathFromUrl, uploadFile } from '@/lib/storage';
import { Point, ToolType, TextElement, StickyNote, PathData, Layer, StoredDrawing, GridMode, CANVAS_WIDTH, CANVAS_HEIGHT, NOTE_W, NOTE_H, NOTE_OFFSET_X, NOTE_OFFSET_Y } from './types';
import { createDefaultLayer, isFillableTool, isShapeTool, renderSvg } from './utils';

export function useDrawingBoard(projectId: string) {
  const [drawings, setDrawings] = useState<StoredDrawing[]>([]);
  const [showEditor, setShowEditor] = useState(false);
  const [drawingTitle, setDrawingTitle] = useState('');
  const [lightbox, setLightbox] = useState<{ svg: string; title: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stylusMode, setStylusMode] = useState(false);
  const [pressureEnabled, setPressureEnabled] = useState(true);

  // SVG Editor State
  const svgRef = useRef<SVGSVGElement>(null);
  const [svgMounted, setSvgMounted] = useState(false);
  const svgCallbackRef = useCallback((node: SVGSVGElement | null) => {
    (svgRef as React.MutableRefObject<SVGSVGElement | null>).current = node;
    setSvgMounted(!!node);
  }, []);
  const [layers, setLayers] = useState<Layer[]>([createDefaultLayer(0)]);
  const [activeLayerId, setActiveLayerId] = useState<string>('');
  const [currentPath, setCurrentPath] = useState<PathData | null>(null);
  const [, setIsDrawing] = useState(false);
  // Refs to avoid stale closure in pointer event handlers
  const isDrawingRef = useRef(false);
  const currentPathRef = useRef<PathData | null>(null);
  // Refs for values needed inside native event listeners
  const activeLayerIdRef = useRef('');
  const brushColorRef = useRef('#1c1917');
  const brushSizeRef = useRef(3);
  const brushOpacityRef = useRef(1);
  const toolRef = useRef<ToolType>('pen');
  const pressureEnabledRef = useRef(true);
  const fillEnabledRef = useRef(false);
  const layersRef = useRef<Layer[]>([]);
  const [brushColor, setBrushColor] = useState('#1c1917');
  const [brushSize, setBrushSize] = useState(3);
  const [brushOpacity, setBrushOpacity] = useState(1);
  const [tool, setTool] = useState<ToolType>('pen');
  const [history, setHistory] = useState<Layer[][]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [renamingLayerId, setRenamingLayerId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [fillEnabled, setFillEnabled] = useState(false);
  const [gridMode, setGridMode] = useState<GridMode>('none');
  const [gridSize, setGridSize] = useState(20);
  const [canvasColor, setCanvasColor] = useState('white');

  // Text Elements State
  const [textElements, setTextElements] = useState<TextElement[]>([]);
  const [editingTextId, setEditingTextId] = useState<string | null>(null);
  const [editingTextValue, setEditingTextValue] = useState('');
  const [textFontSize, setTextFontSize] = useState(20);
  const [draggingText, setDraggingText] = useState<{ id: string; startX: number; startY: number; origX: number; origY: number } | null>(null);

  // Sticky Notes State
  const [notes, setNotes] = useState<StickyNote[]>([]);
  const [noteColorIndex, setNoteColorIndex] = useState(0);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editingNoteText, setEditingNoteText] = useState('');
  const [draggingNote, setDraggingNote] = useState<{ id: string; startX: number; startY: number; origNoteX: number; origNoteY: number } | null>(null);

  // Keep refs in sync with state
  useEffect(() => { activeLayerIdRef.current = activeLayerId; }, [activeLayerId]);
  useEffect(() => { brushColorRef.current = brushColor; }, [brushColor]);
  useEffect(() => { brushSizeRef.current = brushSize; }, [brushSize]);
  useEffect(() => { brushOpacityRef.current = brushOpacity; }, [brushOpacity]);
  useEffect(() => { toolRef.current = tool; }, [tool]);
  useEffect(() => { pressureEnabledRef.current = pressureEnabled; }, [pressureEnabled]);
  useEffect(() => { fillEnabledRef.current = fillEnabled; }, [fillEnabled]);
  useEffect(() => { layersRef.current = layers; }, [layers]);

  // Initialize active layer
  useEffect(() => {
    if (layers.length > 0 && !activeLayerId) {
      setActiveLayerId(layers[0].id);
    }
  }, [layers, activeLayerId]);

  // ESC key exits fullscreen
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen]);

  // Lock body scroll in fullscreen
  useEffect(() => {
    if (isFullscreen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isFullscreen]);

  // Reset white brush if background is not black
  useEffect(() => {
    if (canvasColor !== 'black' && brushColor === '#ffffff') {
      setBrushColor('#1c1917');
    }
  }, [canvasColor, brushColor]);

  // Fetch drawings from database
  useEffect(() => {
    async function fetchDrawings() {
      setLoading(true);
      setError('');

      const { data, error: fetchError } = await supabase
        .from('drawings')
        .select('*')
        .eq('project_id', projectId)
        .order('created_at', { ascending: false });

      if (fetchError) {
        setError('Çizimler yüklenirken bir hata oluştu');
        setLoading(false);
        return;
      }

      setDrawings(data || []);
      setLoading(false);
    }

    fetchDrawings();
  }, [projectId]);

  const getActiveLayer = useCallback((): Layer | undefined => {
    return layers.find((l) => l.id === activeLayerId);
  }, [layers, activeLayerId]);

  const getSvgPoint = useCallback((e: React.PointerEvent | PointerEvent): Point => {
    const svg = svgRef.current;
    if (!svg) return { x: 0, y: 0 };

    // Use SVG's own coordinate transformation matrix for pixel-perfect accuracy
    // This correctly handles zoom, scroll, CSS transforms, and viewBox scaling on all devices
    const ctm = svg.getScreenCTM();
    if (ctm) {
      const pt = svg.createSVGPoint();
      pt.x = e.clientX;
      pt.y = e.clientY;
      const svgPt = pt.matrixTransform(ctm.inverse());
      return {
        x: svgPt.x,
        y: svgPt.y,
        pressure: e.pressure || 0.5,
      };
    }

    // Fallback: manual calculation
    const rect = svg.getBoundingClientRect();
    const scaleX = CANVAS_WIDTH / rect.width;
    const scaleY = CANVAS_HEIGHT / rect.height;
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
      pressure: e.pressure || 0.5,
    };
  }, []);

  const pushHistory = useCallback((newLayers: Layer[]) => {
    setHistory((h) => {
      const newHistory = h.slice(0, historyIndex + 1);
      // Deep clone layers for history
      const cloned = newLayers.map((l) => ({
        ...l,
        paths: l.paths.map((p) => ({ ...p, points: [...p.points] })),
      }));
      newHistory.push(cloned);
      // Limit history to 50 states
      if (newHistory.length > 50) {
        newHistory.shift();
        setHistoryIndex((i) => i - 1);
      }
      return newHistory;
    });
    setHistoryIndex((i) => Math.min(i + 1, 49));
  }, [historyIndex]);

  // Point-in-shape hit test for fill tool
  const hitTestShape = useCallback((path: PathData, point: Point): boolean => {
    if (path.points.length < 2) return false;
    if (!isFillableTool(path.tool)) return false;

    const start = path.points[0];
    const end = path.points[path.points.length - 1];
    const x = point.x;
    const y = point.y;

    switch (path.tool) {
      case 'rectangle': {
        const minX = Math.min(start.x, end.x);
        const maxX = Math.max(start.x, end.x);
        const minY = Math.min(start.y, end.y);
        const maxY = Math.max(start.y, end.y);
        return x >= minX && x <= maxX && y >= minY && y <= maxY;
      }
      case 'circle': {
        const cx = (start.x + end.x) / 2;
        const cy = (start.y + end.y) / 2;
        const r = Math.max(Math.abs(end.x - start.x), Math.abs(end.y - start.y)) / 2;
        const dx = x - cx;
        const dy = y - cy;
        return dx * dx + dy * dy <= r * r;
      }
      case 'ellipse': {
        const ecx = (start.x + end.x) / 2;
        const ecy = (start.y + end.y) / 2;
        const rx = Math.abs(end.x - start.x) / 2;
        const ry = Math.abs(end.y - start.y) / 2;
        if (rx === 0 || ry === 0) return false;
        return ((x - ecx) * (x - ecx)) / (rx * rx) + ((y - ecy) * (y - ecy)) / (ry * ry) <= 1;
      }
      case 'triangle': {
        const topX = (start.x + end.x) / 2;
        const topY = Math.min(start.y, end.y);
        const bottomY = Math.max(start.y, end.y);
        const leftX = Math.min(start.x, end.x);
        const rightX = Math.max(start.x, end.x);
        // Barycentric coordinate method
        const ax = topX, ay = topY;
        const bx = rightX, by = bottomY;
        const cx2 = leftX, cy2 = bottomY;
        const denom = (by - cy2) * (ax - cx2) + (cx2 - bx) * (ay - cy2);
        if (denom === 0) return false;
        const a = ((by - cy2) * (x - cx2) + (cx2 - bx) * (y - cy2)) / denom;
        const b = ((cy2 - ay) * (x - cx2) + (ax - cx2) * (y - cy2)) / denom;
        const c = 1 - a - b;
        return a >= 0 && b >= 0 && c >= 0;
      }
      default:
        return false;
    }
  }, []);



  // Native pointer event handlers — bypass React synthetic event system for iPad reliability
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;

    const onDown = (e: PointerEvent) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      e.preventDefault();

      const currentTool = toolRef.current;
      const currentLayers = layersRef.current;
      const currentActiveLayerId = activeLayerIdRef.current;
      const activeLayer = currentLayers.find((l) => l.id === currentActiveLayerId);
      if (!activeLayer || activeLayer.locked) return;

      if (e.pointerType === 'pen') setStylusMode(true);

      const point = getSvgPoint(e);

      // note / text / fill tools are handled by React synthetic events (they need state)
      if (currentTool === 'note' || currentTool === 'text' || currentTool === 'fill') return;

      const pressure = pressureEnabledRef.current && e.pointerType === 'pen' ? (e.pressure || 0.5) : 1;
      const adjustedWidth = currentTool === 'pen' || currentTool === 'eraser'
        ? Math.max(1, brushSizeRef.current * pressure)
        : brushSizeRef.current;

      const newPath: PathData = {
        id: `p${Date.now()}`,
        points: [point],
        color: brushColorRef.current,
        width: adjustedWidth,
        widths: currentTool === 'pen' ? [adjustedWidth] : undefined,
        tool: currentTool,
        fill: fillEnabledRef.current && isFillableTool(currentTool) ? brushColorRef.current : null,
        opacity: brushOpacityRef.current,
      };

      currentPathRef.current = newPath;
      isDrawingRef.current = true;
      setCurrentPath(newPath);
      setIsDrawing(true);

      try { svg.setPointerCapture(e.pointerId); } catch (err) { void err; }
    };

    const onMove = (e: PointerEvent) => {
      if (!isDrawingRef.current || !currentPathRef.current) return;
      e.preventDefault();

      const currentTool = currentPathRef.current.tool;
      if (currentTool === 'fill' || currentTool === 'note' || currentTool === 'text') return;

      const currentLayers = layersRef.current;
      const currentActiveLayerId = activeLayerIdRef.current;
      const activeLayer = currentLayers.find((l) => l.id === currentActiveLayerId);
      if (!activeLayer || activeLayer.locked) return;

      const point = getSvgPoint(e);

      if (isShapeTool(currentTool)) {
        const updated = { ...currentPathRef.current, points: [currentPathRef.current.points[0], point] };
        currentPathRef.current = updated;
        setCurrentPath(updated);
      } else {
        const pressure = pressureEnabledRef.current && e.pointerType === 'pen' ? (e.pressure || 0.5) : 1;
        const adjustedWidth = Math.max(1, brushSizeRef.current * pressure);
        const prev = currentPathRef.current;
        const lastPt = prev.points[prev.points.length - 1];
        const minDist = e.pointerType === 'pen' ? 1.5 : 2.5;
        const dx = point.x - lastPt.x;
        const dy = point.y - lastPt.y;
        if (dx * dx + dy * dy < minDist * minDist) return;

        const prevWidths = prev.widths ?? [prev.width];
        const lastWidth = prevWidths[prevWidths.length - 1] ?? prev.width;
        const smoothedWidth = lastWidth * 0.6 + adjustedWidth * 0.4;

        const updated = {
          ...prev,
          points: [...prev.points, point],
          width: smoothedWidth,
          widths: prev.widths ? [...prev.widths, smoothedWidth] : undefined,
        };
        currentPathRef.current = updated;
        setCurrentPath(updated);
      }
    };

    const onUp = (e: PointerEvent) => {
      if (!isDrawingRef.current || !currentPathRef.current) return;
      e.preventDefault();

      try { svg.releasePointerCapture(e.pointerId); } catch (err) { void err; }

      const finalPath = { ...currentPathRef.current };
      const currentActiveLayerId = activeLayerIdRef.current;

      if (isShapeTool(finalPath.tool) && finalPath.points.length < 2) {
        currentPathRef.current = null;
        isDrawingRef.current = false;
        setCurrentPath(null);
        setIsDrawing(false);
        return;
      }

      setLayers((prev) => {
        const newLayers = prev.map((layer) =>
          layer.id === currentActiveLayerId
            ? { ...layer, paths: [...layer.paths, finalPath] }
            : layer
        );
        pushHistory(newLayers);
        return newLayers;
      });

      currentPathRef.current = null;
      isDrawingRef.current = false;
      setCurrentPath(null);
      setIsDrawing(false);
    };

    // ─── iOS/iPadOS context-menu suppression ────────────────────────────────
    // On iPad with Apple Pencil, a brief pause between strokes is interpreted
    // as a long-press, triggering the native "Copy / Search with Google" menu.
    // We block all the events that lead to this popup at the native layer.
    const blockDefault = (e: Event) => e.preventDefault();

    svg.addEventListener('pointerdown', onDown, { passive: false });
    svg.addEventListener('pointermove', onMove, { passive: false });
    svg.addEventListener('pointerup', onUp, { passive: false });
    svg.addEventListener('pointercancel', onUp, { passive: false });
    // Suppress iOS context menu & selection callout
    svg.addEventListener('contextmenu', blockDefault, { passive: false });
    svg.addEventListener('touchstart', blockDefault, { passive: false });
    svg.addEventListener('touchend', blockDefault, { passive: false });
    svg.addEventListener('selectstart', blockDefault, { passive: false });

    return () => {
      svg.removeEventListener('pointerdown', onDown);
      svg.removeEventListener('pointermove', onMove);
      svg.removeEventListener('pointerup', onUp);
      svg.removeEventListener('pointercancel', onUp);
      svg.removeEventListener('contextmenu', blockDefault);
      svg.removeEventListener('touchstart', blockDefault);
      svg.removeEventListener('touchend', blockDefault);
      svg.removeEventListener('selectstart', blockDefault);
    };
  }, [getSvgPoint, pushHistory, svgMounted]);

  // React synthetic handlers — only for note/text/fill tools
  const startDrawing = useCallback((e: React.PointerEvent) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    e.preventDefault();

    const point = getSvgPoint(e);

    if (tool === 'text') {
      const clickedText = textElements.find((t) => {
        const approxWidth = t.text.length * t.fontSize * 0.6;
        const approxHeight = t.fontSize * 1.4;
        return point.x >= t.x - 4 && point.x <= t.x + approxWidth + 4
          && point.y >= t.y - approxHeight && point.y <= t.y + 4;
      });
      if (clickedText) {
        setEditingTextId(clickedText.id);
        setEditingTextValue(clickedText.text);
        return;
      }
      const newText: TextElement = {
        id: `text-${Date.now()}`,
        x: point.x,
        y: point.y,
        text: '',
        color: brushColor,
        fontSize: textFontSize,
        fontFamily: 'sans-serif',
      };
      setTextElements((prev) => [...prev, newText]);
      setEditingTextId(newText.id);
      setEditingTextValue('');
      return;
    }

    if (tool === 'note') {
      const newNote: StickyNote = {
        id: `note-${Date.now()}`,
        anchorX: point.x,
        anchorY: point.y,
        noteX: Math.min(point.x + NOTE_OFFSET_X, CANVAS_WIDTH - NOTE_W - 4),
        noteY: Math.max(point.y + NOTE_OFFSET_Y, 4),
        text: '',
        colorIndex: noteColorIndex,
      };
      setNotes((prev) => [...prev, newNote]);
      setEditingNoteId(newNote.id);
      setEditingNoteText('');
      return;
    }

    if (tool === 'fill') {
      const allPaths: { path: PathData; layerId: string; pathIndex: number }[] = [];
      for (let li = layers.length - 1; li >= 0; li--) {
        const layer = layers[li];
        if (!layer.visible || layer.locked) continue;
        for (let pi = layer.paths.length - 1; pi >= 0; pi--) {
          allPaths.push({ path: layer.paths[pi], layerId: layer.id, pathIndex: pi });
        }
      }
      for (const { path, layerId, pathIndex } of allPaths) {
        if (hitTestShape(path, point)) {
          const newFill = path.fill && path.fill === brushColor ? null : brushColor;
          const newLayers = layers.map((layer) => {
            if (layer.id === layerId) {
              const newPaths = [...layer.paths];
              newPaths[pathIndex] = { ...path, fill: newFill };
              return { ...layer, paths: newPaths };
            }
            return layer;
          });
          setLayers(newLayers);
          pushHistory(newLayers);
          return;
        }
      }
    }
  }, [getSvgPoint, brushColor, tool, textFontSize, textElements, noteColorIndex, layers, hitTestShape, pushHistory]);

  // draw and stopDrawing are now handled natively — these are no-ops for non-note/text/fill tools
  const draw = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
  }, []);

  const stopDrawing = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
  }, []);

  const undo = () => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      setLayers(history[newIndex]);
    } else if (historyIndex === 0) {
      setHistoryIndex(-1);
      setLayers([createDefaultLayer(0)]);
      setActiveLayerId(layers[0]?.id || '');
    }
  };

  const redo = () => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      setLayers(history[newIndex]);
    }
  };

  const clearCanvas = () => {
    const clearedLayers = layers.map((layer) => ({ ...layer, paths: [] }));
    setLayers(clearedLayers);
    pushHistory(clearedLayers);
    setNotes([]);
    setEditingNoteId(null);
    setTextElements([]);
    setEditingTextId(null);
  };

  // Layer operations
  const addLayer = () => {
    const newLayer = createDefaultLayer(layers.length);
    const newLayers = [...layers, newLayer];
    setLayers(newLayers);
    setActiveLayerId(newLayer.id);
    pushHistory(newLayers);
  };

  const deleteLayer = (layerId: string) => {
    if (layers.length <= 1) {
      setError('En az bir katman kalmalıdır');
      setTimeout(() => setError(''), 3000);
      return;
    }
    const newLayers = layers.filter((l) => l.id !== layerId);
    setLayers(newLayers);
    if (activeLayerId === layerId) {
      setActiveLayerId(newLayers[0]?.id || '');
    }
    pushHistory(newLayers);
  };

  const toggleLayerVisibility = (layerId: string) => {
    const newLayers = layers.map((l) =>
      l.id === layerId ? { ...l, visible: !l.visible } : l
    );
    setLayers(newLayers);
    pushHistory(newLayers);
  };

  const toggleLayerLock = (layerId: string) => {
    const newLayers = layers.map((l) =>
      l.id === layerId ? { ...l, locked: !l.locked } : l
    );
    setLayers(newLayers);
    pushHistory(newLayers);
  };

  const moveLayer = (layerId: string, direction: 'up' | 'down') => {
    const index = layers.findIndex((l) => l.id === layerId);
    if (index === -1) return;
    if (direction === 'up' && index === layers.length - 1) return;
    if (direction === 'down' && index === 0) return;

    const newLayers = [...layers];
    const targetIndex = direction === 'up' ? index + 1 : index - 1;
    [newLayers[index], newLayers[targetIndex]] = [newLayers[targetIndex], newLayers[index]];
    setLayers(newLayers);
    pushHistory(newLayers);
  };

  const startRename = (layer: Layer) => {
    setRenamingLayerId(layer.id);
    setRenameValue(layer.name);
  };

  const confirmRename = () => {
    if (!renamingLayerId || !renameValue.trim()) {
      setRenamingLayerId(null);
      return;
    }
    const newLayers = layers.map((l) =>
      l.id === renamingLayerId ? { ...l, name: renameValue.trim() } : l
    );
    setLayers(newLayers);
    pushHistory(newLayers);
    setRenamingLayerId(null);
    setRenameValue('');
  };

  const saveDrawing = async () => {
    const allPathsCount = layers.reduce((sum, l) => sum + l.paths.length, 0);
    if (allPathsCount === 0 && !currentPath) {
      setError('Kaydedilecek çizim bulunmuyor');
      return;
    }

    setSaving(true);
    setError('');

    // Include current path in active layer for rendering
    let renderLayers = layers;
    if (currentPath) {
      renderLayers = layers.map((layer) => {
        if (layer.id === activeLayerId) {
          return { ...layer, paths: [...layer.paths, currentPath] };
        }
        return layer;
      });
    }

    const svgString = renderSvg(renderLayers, notes, textElements);
    const title = drawingTitle || 'Yeni çizim';

    // Convert SVG string to Blob for upload
    const blob = new Blob([svgString], { type: 'image/svg+xml' });
    const file = new File([blob], `${title.replace(/\s+/g, '_')}.svg`, { type: 'image/svg+xml' });

    const { url, error: uploadError } = await uploadFile(file, projectId, 'drawings');

    if (uploadError || !url) {
      const errorMsg = uploadError?.message || 'Bilinmeyen hata';
      if (errorMsg.includes('bucket') || errorMsg.includes('Bucket')) {
        setError('Storage bucket bulunamadı. Lütfen admin ile iletişime geçin.');
      } else if (errorMsg.includes('auth') || errorMsg.includes('Auth')) {
        setError('Oturum süreniz dolmuş. Lütfen tekrar giriş yapın.');
      } else {
        setError(`Çizim kaydedilirken hata: ${errorMsg}`);
      }
      setSaving(false);
      return;
    }

    // Insert into database
    const { data: inserted, error: dbError } = await supabase
      .from('drawings')
      .insert({
        project_id: projectId,
        drawing_data: url,
        title: title,
      })
      .select()
      .single();

    if (dbError) {
      // Clean up uploaded file
      const path = getFilePathFromUrl(url);
      if (path) await deleteFile(path);
      setError('Çizim kaydedilirken hata oluştu. Lütfen tekrar deneyin.');
      setSaving(false);
      return;
    }

    if (inserted) {
      setDrawings((prev) => [inserted, ...prev]);
    }

    // Reset editor
    resetEditor();
    setSaving(false);
  };

  const resetEditor = () => {
    setShowEditor(false);
    setIsFullscreen(false);
    setDrawingTitle('');
    const defaultLayer = createDefaultLayer(0);
    setLayers([defaultLayer]);
    setActiveLayerId(defaultLayer.id);
    setCurrentPath(null);
    setHistory([]);
    setHistoryIndex(-1);
    setStylusMode(false);
    setFillEnabled(false);
    setTool('pen');
    setGridMode('none');
    setGridSize(20);
    setCanvasColor('white');
    setNotes([]);
    setEditingNoteId(null);
    setDraggingNote(null);
    setTextElements([]);
    setEditingTextId(null);
    setDraggingText(null);
  };

  // Note drag handlers (SVG coordinate space)
  const handleNoteDragStart = useCallback((e: React.PointerEvent, noteId: string) => {
    e.stopPropagation();
    e.preventDefault();
    const svg = svgRef.current;
    if (!svg) return;
    const ctm = svg.getScreenCTM();
    let svgX = e.clientX;
    let svgY = e.clientY;
    if (ctm) {
      const pt = svg.createSVGPoint();
      pt.x = e.clientX;
      pt.y = e.clientY;
      const sp = pt.matrixTransform(ctm.inverse());
      svgX = sp.x;
      svgY = sp.y;
    }
    const note = notes.find((n) => n.id === noteId);
    if (!note) return;
    setDraggingNote({ id: noteId, startX: svgX, startY: svgY, origNoteX: note.noteX, origNoteY: note.noteY });
    (e.target as Element).setPointerCapture?.(e.pointerId);
  }, [notes]);

  const handleNoteDragMove = useCallback((e: React.PointerEvent) => {
    if (!draggingNote) return;
    e.stopPropagation();
    const svg = svgRef.current;
    if (!svg) return;
    const ctm = svg.getScreenCTM();
    let svgX = e.clientX;
    let svgY = e.clientY;
    if (ctm) {
      const pt = svg.createSVGPoint();
      pt.x = e.clientX;
      pt.y = e.clientY;
      const sp = pt.matrixTransform(ctm.inverse());
      svgX = sp.x;
      svgY = sp.y;
    }
    const dx = svgX - draggingNote.startX;
    const dy = svgY - draggingNote.startY;
    setNotes((prev) => prev.map((n) =>
      n.id === draggingNote.id
        ? { ...n, noteX: Math.max(0, Math.min(CANVAS_WIDTH - NOTE_W, draggingNote.origNoteX + dx)), noteY: Math.max(0, Math.min(CANVAS_HEIGHT - NOTE_H, draggingNote.origNoteY + dy)) }
        : n
    ));
  }, [draggingNote]);

  const handleNoteDragEnd = useCallback((e: React.PointerEvent) => {
    if (!draggingNote) return;
    e.stopPropagation();
    (e.target as Element).releasePointerCapture?.(e.pointerId);
    setDraggingNote(null);
  }, [draggingNote]);

  const deleteNote = useCallback((noteId: string) => {
    setNotes((prev) => prev.filter((n) => n.id !== noteId));
    if (editingNoteId === noteId) setEditingNoteId(null);
  }, [editingNoteId]);

  const confirmNoteEdit = useCallback(() => {
    if (!editingNoteId) return;
    setNotes((prev) => prev.map((n) =>
      n.id === editingNoteId ? { ...n, text: editingNoteText } : n
    ));
    setEditingNoteId(null);
  }, [editingNoteId, editingNoteText]);

  // Text element handlers
  const confirmTextEdit = useCallback(() => {
    if (!editingTextId) return;
    setTextElements((prev) => {
      const updated = prev.map((t) =>
        t.id === editingTextId ? { ...t, text: editingTextValue } : t
      );
      // Remove empty text elements
      return updated.filter((t) => t.text.trim() !== '');
    });
    setEditingTextId(null);
  }, [editingTextId, editingTextValue]);

  const deleteTextElement = useCallback((textId: string) => {
    setTextElements((prev) => prev.filter((t) => t.id !== textId));
    if (editingTextId === textId) setEditingTextId(null);
  }, [editingTextId]);

  const handleTextDragStart = useCallback((e: React.PointerEvent, textId: string) => {
    e.stopPropagation();
    e.preventDefault();
    const svg = svgRef.current;
    if (!svg) return;
    const ctm = svg.getScreenCTM();
    let svgX = e.clientX;
    let svgY = e.clientY;
    if (ctm) {
      const pt = svg.createSVGPoint();
      pt.x = e.clientX;
      pt.y = e.clientY;
      const sp = pt.matrixTransform(ctm.inverse());
      svgX = sp.x;
      svgY = sp.y;
    }
    const textEl = textElements.find((t) => t.id === textId);
    if (!textEl) return;
    setDraggingText({ id: textId, startX: svgX, startY: svgY, origX: textEl.x, origY: textEl.y });
    (e.target as Element).setPointerCapture?.(e.pointerId);
  }, [textElements]);

  const handleTextDragMove = useCallback((e: React.PointerEvent) => {
    if (!draggingText) return;
    e.stopPropagation();
    const svg = svgRef.current;
    if (!svg) return;
    const ctm = svg.getScreenCTM();
    let svgX = e.clientX;
    let svgY = e.clientY;
    if (ctm) {
      const pt = svg.createSVGPoint();
      pt.x = e.clientX;
      pt.y = e.clientY;
      const sp = pt.matrixTransform(ctm.inverse());
      svgX = sp.x;
      svgY = sp.y;
    }
    const dx = svgX - draggingText.startX;
    const dy = svgY - draggingText.startY;
    setTextElements((prev) => prev.map((t) =>
      t.id === draggingText.id
        ? { ...t, x: draggingText.origX + dx, y: draggingText.origY + dy }
        : t
    ));
  }, [draggingText]);

  const handleTextDragEnd = useCallback((e: React.PointerEvent) => {
    if (!draggingText) return;
    e.stopPropagation();
    (e.target as Element).releasePointerCapture?.(e.pointerId);
    setDraggingText(null);
  }, [draggingText]);

  const handleDelete = async (drawing: StoredDrawing) => {
    // Delete from storage first — if this fails, abort to avoid orphaned DB records
    const path = drawing.storage_path || getFilePathFromUrl(drawing.drawing_data);
    if (path) {
      const { error: storageError } = await deleteFile(path);
      if (storageError) {
        setError('Çizim storage\'dan silinirken bir hata oluştu. Lütfen tekrar deneyin.');
        return;
      }
    }

    // Only delete from database after storage is confirmed deleted
    const { error: dbError } = await supabase
      .from('drawings')
      .delete()
      .eq('id', drawing.id);

    if (dbError) {
      setError('Çizim veritabanından silinirken bir hata oluştu');
      return;
    }

    setDrawings((prev) => prev.filter((d) => d.id !== drawing.id));
  };


  return {
    drawings, setDrawings, showEditor, setShowEditor, drawingTitle, setDrawingTitle,
    lightbox, setLightbox, saving, setSaving, loading, setLoading, error, setError,
    stylusMode, setStylusMode, pressureEnabled, setPressureEnabled, svgRef, svgMounted, setSvgMounted,
    svgCallbackRef, layers, setLayers, activeLayerId, setActiveLayerId, currentPath, setCurrentPath,
    brushColor, setBrushColor, brushSize, setBrushSize, brushOpacity, setBrushOpacity, tool, setTool,
    history, setHistory, historyIndex, setHistoryIndex, renamingLayerId, setRenamingLayerId, renameValue, setRenameValue,
    isFullscreen, setIsFullscreen, fillEnabled, setFillEnabled, gridMode, setGridMode, gridSize, setGridSize,
    canvasColor, setCanvasColor,
    textElements, setTextElements, editingTextId, setEditingTextId, editingTextValue, setEditingTextValue,
    textFontSize, setTextFontSize, draggingText, setDraggingText, notes, setNotes, noteColorIndex, setNoteColorIndex,
    editingNoteId, setEditingNoteId, editingNoteText, setEditingNoteText, draggingNote, setDraggingNote,
    getActiveLayer, pushHistory, startDrawing, draw, stopDrawing, undo, redo, clearCanvas, addLayer, deleteLayer,
    toggleLayerVisibility, toggleLayerLock, moveLayer, startRename, confirmRename, saveDrawing, resetEditor,
    handleNoteDragStart, handleNoteDragMove, handleNoteDragEnd, deleteNote, confirmNoteEdit, confirmTextEdit,
    deleteTextElement, handleTextDragStart, handleTextDragMove, handleTextDragEnd, handleDelete
  };
}
